// API base URL from environment variables - use import.meta.env for Vite
const API_BASE_URL =
  import.meta.env.VITE_API_URL || "http://localhost:4000/api/v1";

// Default request timeout in milliseconds
const DEFAULT_TIMEOUT = 30000;

// HTTP request methods
type HttpMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

// Interface for API request options
interface ApiRequestOptions {
  method?: HttpMethod;
  headers?: Record<string, string>;
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined>;
  timeout?: number;
  withCredentials?: boolean;
  skipAuthCheck?: boolean; // Skip auth check for auth-related endpoints
}

// Interface for API response
interface ApiResponse<T = unknown> {
  data: T;
  status: number;
  headers: Headers;
}

// Error class for API errors
export class ApiError extends Error {
  status: number;
  data: unknown;

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

// Storage keys (must match those in auth.ts)
const TOKEN_KEY = "authToken";
const USER_KEY = "authUser";
const TOKEN_EXPIRY_KEY = "authTokenExpires";

// List of auth-related endpoints that should not clear tokens on 401 errors
const AUTH_ENDPOINTS = [
  "auth/login",
  "auth/register",
  "auth/refresh-token",
  "auth/validate-token",
];

// Function to check if an endpoint is auth-related
const isAuthEndpoint = (url: string): boolean => {
  return AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));
};

// Function to handle API errors
const handleApiError = (error: unknown, url: string): never => {
  if (error instanceof ApiError) {
    // Handle specific HTTP status codes
    switch (error.status) {
      case 401: {
        // Unauthorized - redirect to login, but only if we're not already on the login page
        // and not trying to refresh the token
        // Unauthorized - redirect to login, but only if we're not already on the login page
        // and not trying to refresh the token

        // Log the auth redirection attempt
        console.log(
          "401 Unauthorized response - Current path:",
          window.location.pathname,
        );

        // Don't redirect if we just came from login or if we're already on login page
        const justLoggedIn = sessionStorage.getItem("justLoggedIn") === "true";
        if (justLoggedIn) {
          console.log("Skipping redirect to login because user just logged in");
          sessionStorage.removeItem("justLoggedIn");
        } else if (
          !window.location.pathname.includes("/login") &&
          !isAuthEndpoint(url)
        ) {
          // Only clear auth data for non-auth endpoints
          if (!isAuthEndpoint(url)) {
            // Clear auth data to prevent infinite loops
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(USER_KEY);
            localStorage.removeItem(TOKEN_EXPIRY_KEY);

            // Dispatch event to notify other parts of the app (like AuthContext)
            window.dispatchEvent(new CustomEvent("auth-token-cleared"));

            console.log(
              "Auth data cleared due to 401 response on non-auth endpoint",
            );
          } else {
            console.log(
              "Not clearing auth data for auth-related endpoint:",
              url,
            );
          }

          // Only redirect if we're not already on the login page
          if (window.location.pathname !== "/login") {
            console.log("Redirecting to login page due to 401 response");
            window.location.href = "/login";
          }
        }
        break;
      }
      case 403:
        // Forbidden - user doesn't have permission
        console.error("Permission denied:", error.message);
        break;
      case 404:
        // Not found
        console.error("Resource not found:", error.message);
        break;
      case 500:
        // Server error
        console.error("Server error:", error.message);

        // Check for database initialization errors
        if (
          error instanceof Error &&
          error.message &&
          ((error.message.includes("table") &&
            error.message.includes("does not exist")) ||
            error.message.includes("Invalid `prisma"))
        ) {
          throw new ApiError(
            "Database not initialized. The API server is running but the database is not set up.",
            500,
            error,
          );
        }
        break;
      default:
        console.error(
          `API Error (${error.status}):`,
          error instanceof Error ? error.message : error,
        );
    }
    throw error;
  }

  // Network or other errors
  console.error("API Request failed:", error);

  // Check for CORS errors
  if (
    error instanceof Error &&
    error.message &&
    (error.message.includes("CORS") ||
      error.message.includes("NetworkError") ||
      error.message.includes("Failed to fetch") ||
      error.message.includes("Network request failed"))
  ) {
    throw new ApiError("CORS error: Unable to connect to API server", 0, error);
  }

  const errorMessage =
    error instanceof Error ? error.message : "Unknown error occurred";
  throw new ApiError(errorMessage, 0);
};

// Check if token is expired (must match the function in auth.ts)
const isTokenExpired = (): boolean => {
  const expiresAt = localStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expiresAt) {
    console.log("Token expiry check - No expiry data found");
    return true;
  }

  // Add a 5-minute buffer to prevent edge cases
  const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds
  const expired = Date.now() > parseInt(expiresAt) - bufferTime;
  console.log(
    "Token expiry check - Expires at:",
    new Date(parseInt(expiresAt)).toLocaleString(),
    "Expired:",
    expired,
  );
  return expired;
};

// Helper to ensure the endpoint has the correct format
const normalizeEndpoint = (endpoint: string): string => {
  // If it's already a full URL, return it as is
  if (endpoint.startsWith("http")) {
    return endpoint;
  }

  // Clean the path by removing leading/trailing slashes and redundant prefixes
  let cleanPath = endpoint.trim();

  // Aggressively remove redundant prefixes that might have been hardcoded in services
  cleanPath = cleanPath.replace(/^(\/)?api\/v1\//, "/");
  cleanPath = cleanPath.replace(/^(\/)?v1\//, "/");

  // Ensure it starts with a single slash
  if (!cleanPath.startsWith("/")) {
    cleanPath = `/${cleanPath}`;
  }

  // Get base URL and remove trailing slash if present
  const baseUrl = API_BASE_URL.replace(/\/+$/, "");

  // Combine base URL and cleaned path
  const finalUrl = `${baseUrl}${cleanPath}`;

  // Final safety check to remove any accidental double slashes (except after protocol)
  return finalUrl.replace(/([^:])\/+/g, "$1/");
};

// Main API request function
export async function apiRequest<T = unknown>(
  endpoint: string,
  options: ApiRequestOptions = {},
): Promise<ApiResponse<T>> {
  const {
    method = "GET",
    headers = {},
    body,
    timeout = DEFAULT_TIMEOUT,
    withCredentials = false,
    skipAuthCheck = false,
  } = options;

  // Normalize the endpoint
  let url = normalizeEndpoint(endpoint);

  // Add query parameters if provided
  if (options.params) {
    const urlObj = new URL(url);
    Object.entries(options.params).forEach(([key, value]) => {
      if (value !== undefined) {
        urlObj.searchParams.append(key, String(value));
      }
    });
    url = urlObj.toString();
  }

  // Prepare headers with defaults
  const requestHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...headers,
  };

  // Get auth token from localStorage if available
  const token = localStorage.getItem(TOKEN_KEY);

  // Determine if this is an auth-related endpoint
  const isAuthRelatedEndpoint = isAuthEndpoint(url);

  // Skip auth check for auth-related endpoints if specified
  if (!skipAuthCheck && !isAuthRelatedEndpoint) {
    if (token) {
      // Always attach the token — let the server decide if it's expired
      requestHeaders["Authorization"] = `Bearer ${token}`;
      console.log(
        `API Request to ${endpoint} - Authorization header added with token`,
      );
    }
  } else if (token) {
    // Always add token for auth endpoints if it exists
    requestHeaders["Authorization"] = `Bearer ${token}`;
    console.log(
      `Auth endpoint ${endpoint} - Authorization header added with token`,
    );
  }

  // Prepare request options
  const requestOptions: RequestInit = {
    method,
    headers: requestHeaders,
    credentials: withCredentials ? "include" : "same-origin",
  };

  // Add body for non-GET requests
  if (method !== "GET" && body !== undefined) {
    // Check if body is FormData
    if (body instanceof FormData) {
      requestOptions.body = body;
      // Remove Content-Type header to let the browser set it with the boundary
      delete requestHeaders["Content-Type"];
    } else {
      requestOptions.body = JSON.stringify(body);
    }
  }

  try {
    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    requestOptions.signal = controller.signal;

    // Log the request details for debugging
    console.log(`Making API request: ${method} ${url}`, {
      withCredentials,
      hasToken: !!token,
      isExpired: token ? isTokenExpired() : null,
    });

    // Make the fetch request
    const response = await fetch(url, requestOptions);
    clearTimeout(timeoutId);

    // Parse response data
    let data: T;
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      data = await response.json();
    } else {
      data = (await response.text()) as unknown as T;
    }

    // Handle error responses
    if (!response.ok) {
      const errorMessage =
        typeof data === "object" && data !== null && "message" in data
          ? String(data.message)
          : response.statusText;

      throw new ApiError(errorMessage, response.status, data);
    }

    // Return successful response
    return {
      data,
      status: response.status,
      headers: response.headers,
    };
  } catch (error: unknown) {
    // Handle request timeout
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new ApiError("Request timeout", 408);
    }

    return handleApiError(error, url);
  }
}

// Convenience methods for different HTTP verbs
export const api = {
  get: <T = unknown>(
    endpoint: string,
    options?: Omit<ApiRequestOptions, "method" | "body">,
  ) => apiRequest<T>(endpoint, { ...options, method: "GET" }),

  post: <T = unknown>(
    endpoint: string,
    body?: unknown,
    options?: Omit<ApiRequestOptions, "method">,
  ) => apiRequest<T>(endpoint, { ...options, method: "POST", body }),

  put: <T = unknown>(
    endpoint: string,
    body?: unknown,
    options?: Omit<ApiRequestOptions, "method">,
  ) => apiRequest<T>(endpoint, { ...options, method: "PUT", body }),

  patch: <T = unknown>(
    endpoint: string,
    body?: unknown,
    options?: Omit<ApiRequestOptions, "method">,
  ) => apiRequest<T>(endpoint, { ...options, method: "PATCH", body }),

  delete: <T = unknown>(
    endpoint: string,
    options?: Omit<ApiRequestOptions, "method">,
  ) => apiRequest<T>(endpoint, { ...options, method: "DELETE" }),
};

export default api;
